import { useFetcher } from '@remix-run/react';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import useUser from '~/hooks/useUser';

import Tracked from '../../Tracked';
import type { PostAuthOptions, ScreenProps } from '.';
import ModalHeadings from './ModalHeadings';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const ErrorMessage = ({ isActive }: { isActive: boolean }) => {
  return isActive ? (
    <div className="flex h-5 flex-row content-center items-center gap-1">
      <ErrorIcon />
      <p className="text-xs font-bold text-red-600">
        Email already in use, please try to log in or reset your password
      </p>
    </div>
  ) : (
    <></>
  );
};

type Props = ScreenProps & { setEmail: Dispatch<SetStateAction<string>> };

const SignInScreen = ({ postAuthOptions, setScreen, setEmail }: Props) => {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();

  const errors = fetcher.data?.errors;
  const success = fetcher.data?.success;
  const submittedEmail = fetcher.data?.email;
  const submitting = fetcher.state === 'submitting';

  useEffect(() => {
    if (!success) return;

    setEmail(submittedEmail!);
    setScreen('signUpComplete');

    if (postAuthOptions?.action === 'restock') {
      storeNotificationData(postAuthOptions);
    }
  });

  return (
    <>
      <ModalHeadings>
        Stay up to date with the latest sneaker drops, raffles and news by
        entering your email below.
      </ModalHeadings>

      <fetcher.Form
        action="/bolt/register/"
        className="flex size-full flex-col items-start gap-4 bg-black"
        method="post"
      >
        <input
          type="hidden"
          value={postAuthOptions?.legacyProductId}
          name="legacyProductId"
        />
        <input type="hidden" value={postAuthOptions?.action} name="action" />
        <input type="hidden" value={user.guestId} name="guestId" />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.email ? 'This is an incorrect email format' : undefined
          }
          id="login-email"
          name="email"
          placeholder="Email address"
          type="email"
        />
        <p className="text-white">
          By continuing you agree to our{' '}
          <a href="/terms-and-conditions/" className="font-bold underline">
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://app.privasee.io/privacy-portal/64ac2c059f43880013f2faf0"
            className="font-bold underline"
          >
            Privacy Policy
          </a>
        </p>
        <Tracked.Button
          className="h-12"
          variant="green-human"
          disabled={submitting}
          name="SIGN_UP_EMAIL"
          pagePosition={'Sign in modal'}
        >
          {submitting ? 'Signing up...' : 'Continue'}
        </Tracked.Button>
        <ErrorMessage isActive={Boolean(errors?.conflict)} />
      </fetcher.Form>
    </>
  );
};

function storeNotificationData(postAuthOptions: PostAuthOptions) {
  localStorage.setItem(
    'notificationData',
    JSON.stringify({
      expires: Date.now() + DAY_IN_MS,
      legacyProductId: postAuthOptions.legacyProductId,
      productName: postAuthOptions.productName,
      sizes: postAuthOptions.sizes
    })
  );
}

export default SignInScreen;
